import type { RouterOutputs } from "@repo/api";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import { ConfigurationContext } from "~/hooks/use-configuration";
import { api } from "~/lib/api";
import { useLoadingStore } from "~/stores/use-loading-store";

export type CashRegisterConfiguration =
  RouterOutputs["config"]["getCashRegisterConfiguration"];

export type MollieConfiguration = RouterOutputs["config"]["getMollieSettings"];

export type PreparationTicketSettings =
  RouterOutputs["config"]["getPreparationTicketSettings"];

export type ReceiptSettings =
  RouterOutputs["config"]["getReceiptSettings"][number];

export type EmailSettings = NonNullable<
  RouterOutputs["config"]["getEmailSettings"]
>;

export type RevenueReportSettings =
  RouterOutputs["config"]["getRevenueReportSettings"];

interface ConfigurationProviderProps {
  children: ReactNode;
}

export const ConfigurationProvider = ({
  children,
}: ConfigurationProviderProps) => {
  const {
    data: cashRegisterConfiguration,
    isFetching: isCashRegisterConfigurationLoading,
  } = api.config.getCashRegisterConfiguration.useQuery();

  const { data: receiptSettings, isFetching: isReceiptSettingsLoading } =
    api.config.getReceiptSettings.useQuery(undefined, {
      initialData: [],
    });

  const {
    data: preparationTicketSettings,
    isFetching: isPreparationTicketSettingsLoading,
  } = api.config.getPreparationTicketSettings.useQuery();

  const { data: revenueReportSettings } =
    api.config.getRevenueReportSettings.useQuery();

  const { data: emailSettings } = api.config.getEmailSettings.useQuery();

  const { data: mollieConfiguration, isFetching: isMollieSettingsLoading } =
    api.config.getMollieSettings.useQuery();

  const { setLoadingStage } = useLoadingStore(
    useShallow((state) => ({
      setLoadingStage: state.setCurrentLoadingStage,
    })),
  );

  useEffect(() => {
    if (
      isCashRegisterConfigurationLoading ||
      isReceiptSettingsLoading ||
      isMollieSettingsLoading ||
      isPreparationTicketSettingsLoading
    ) {
      setLoadingStage("configuration");
    } else {
      setLoadingStage(undefined);
    }
  }, [
    isCashRegisterConfigurationLoading,
    isMollieSettingsLoading,
    isPreparationTicketSettingsLoading,
    isReceiptSettingsLoading,
    setLoadingStage,
  ]);

  if (!cashRegisterConfiguration || !preparationTicketSettings) {
    return <div>Loading configuration...</div>;
  }

  const context = {
    cashRegisterConfiguration,
    receiptSettings,
    mollieConfiguration,
    revenueReportSettings: revenueReportSettings ?? undefined,
    preparationTicketSettings,
    emailSettings: emailSettings ?? undefined,
  };

  return (
    <ConfigurationContext.Provider value={context}>
      {children}
    </ConfigurationContext.Provider>
  );
};
